import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, Pencil, Loader2, PhoneCall, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import SkeletonPage, { SkeletonCard, SkeletonTable } from "@/components/KovaSkeleton";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";

const TONE_META = {
  positive: { chip: "bg-emerald-100 text-[#39FF14] border-emerald-200", label: "Positive" },
  warning:  { chip: "bg-yellow-500/15 text-yellow-200 border-yellow-500/30",       label: "Warning"  },
  danger:   { chip: "bg-red-500/15 text-red-300 border-red-500/25",              label: "Danger"   },
  neutral:  { chip: "bg-[#121815] text-[#F8F9FA] border-[#3A4640]",        label: "Neutral"  },
};

export default function CallOutcomes() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [label, setLabel] = useState("");
  const [order, setOrder] = useState(50);
  const [tone, setTone] = useState("neutral");
  const [isActive, setIsActive] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/call-outcomes?include_inactive=true");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setLabel(""); setOrder(50); setTone("neutral"); setIsActive(true); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setLabel(r.label); setOrder(r.order); setTone(r.tone); setIsActive(r.is_active); setOpen(true); };

  const save = async () => {
    if (!label.trim()) { toast.error("Label is required"); return; }
    setSaving(true);
    try {
      if (editing) await api.put(`/call-outcomes/${editing.id}`, { label, order, tone, is_active: isActive });
      else await api.post("/call-outcomes", { label, order, tone, is_active: isActive });
      toast.success(editing ? "Outcome updated" : "Outcome added");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Deactivate "${r.label}"? Existing interactions using this outcome will remain intact.`)) return;
    try { await api.delete(`/call-outcomes/${r.id}`); toast.success("Deactivated"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  return (
    <div className="p-6 md:p-8" data-testid="call-outcomes-page">
      <PageHeader
        title="Call Outcomes"
        subtitle="Configure the outcomes staff can pick during Customer Engagement calls."
        action={
          <Button className="bg-[#39FF14] hover:bg-[#2ECC71]" onClick={openNew} data-testid="add-outcome-btn">
            <Plus size={16} className="mr-2" /> Add Outcome
          </Button>
        }
      />

      <div className="bg-[#121815] border border-[#3A4640] rounded-lg overflow-hidden">
        <div className="grid grid-cols-[80px,1fr,140px,120px,80px] px-5 py-2.5 border-b border-[#3A4640] bg-[#0B0F0C] text-[10px] uppercase font-heading tracking-wider text-[#8A8F98]">
          <div>Order</div>
          <div>Label</div>
          <div>Tone</div>
          <div>Status</div>
          <div></div>
        </div>
        {loading && <SkeletonTable rows={5} cols={4} />}
        {!loading && rows.length === 0 && (
          <div className="p-8 text-center text-sm text-[#8A8F98]">No outcomes configured yet.</div>
        )}
        {rows.map((r) => {
          const t = TONE_META[r.tone] || TONE_META.neutral;
          return (
            <div key={r.id} className={`grid grid-cols-[80px,1fr,140px,120px,80px] px-5 py-3 border-b border-[#3A4640] last:border-0 items-center ${!r.is_active ? "bg-[#0B0F0C]/60" : "hover:bg-[#0B0F0C]"}`} data-testid={`outcome-row-${r.key}`}>
              <div className="text-xs text-[#8A8F98] font-mono">{r.order}</div>
              <div>
                <div className={`text-sm font-medium ${r.is_active ? "text-[#F8F9FA]" : "text-[#8A8F98] line-through"}`}>{r.label}</div>
                <div className="text-[10px] text-[#8A8F98] font-mono">{r.key}</div>
              </div>
              <div>
                <span className={`text-[10px] uppercase font-heading tracking-wider px-1.5 py-0.5 rounded border ${t.chip}`}>{t.label}</span>
              </div>
              <div>
                {r.is_active ? (
                  <span className="text-[10px] uppercase font-heading tracking-wider text-[#39FF14] bg-emerald-100 border border-emerald-200 px-1.5 py-0.5 rounded">Active</span>
                ) : (
                  <span className="text-[10px] uppercase font-heading tracking-wider text-[#8A8F98] bg-[#1A1F1C] border border-[#3A4640] px-1.5 py-0.5 rounded">Inactive</span>
                )}
              </div>
              <div className="flex items-center justify-end gap-1">
                <button onClick={() => openEdit(r)} className="p-1.5 text-[#8A8F98] hover:text-[#F8F9FA]" title="Edit" data-testid={`edit-outcome-${r.key}`}><Pencil size={13} /></button>
                {r.is_active && (
                  <button onClick={() => remove(r)} className="p-1.5 text-[#8A8F98] hover:text-red-600" title="Deactivate" data-testid={`delete-outcome-${r.key}`}><Trash2 size={13} /></button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="outcome-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit outcome" : "New outcome"}</DialogTitle>
            <DialogDescription>Outcomes appear on the Calling Desk when staff record a call.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Label</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Requested demo" data-testid="outcome-label-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Order</Label>
                <Input type="number" value={order} onChange={(e) => setOrder(parseInt(e.target.value || "50", 10))} data-testid="outcome-order-input" />
              </div>
              <div>
                <Label>Tone</Label>
                <Select value={tone} onValueChange={setTone}>
                  <SelectTrigger data-testid="outcome-tone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="positive">Positive</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="danger">Danger</SelectItem>
                    <SelectItem value="neutral">Neutral</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-[#F8F9FA]">
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} data-testid="outcome-active-checkbox" />
              Active
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="bg-[#39FF14] hover:bg-[#2ECC71]" onClick={save} disabled={saving} data-testid="save-outcome-btn">
              {saving ? <Loader2 size={14} className="animate-spin mr-2" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
